<template>
  <div class="domain-branding-editor">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId }"
      input-id="accent-color"
      class="w-6"
      :errors="v$.accentColor.$errors"
      :label="t('domainPopulator.accentColor')"
    >
      <RygenColorPicker
        :id="inputId"
        v-model="v$.accentColor.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        append-to="body"
      />
    </InputWrapper>

    <InputWrapper input-id="avatar" :label="t('domainPopulator.avatar')">
      <div
        v-if="internalValue?.avatar"
        class="flex justify-content-between align-items-center"
      >
        <DomainAvatar :domain="domain" :domain-branding="internalValue" />
        <RygenButton
          mode="tertiary"
          icon="pi pi-times"
          :aria-label="
            t('common.removeParam', { param: t('domainPopulator.avatar') })
          "
          :severity="Severity.DANGER"
          @click="onRemoveAvatar"
        />
      </div>
      <div
        v-else
        class="upload-container flex flex-column justify-content-center align-items-center"
      >
        <FileUpload
          :show-cancel-button="false"
          :show-upload-button="false"
          custom-upload
          :file-limit="1"
          :choose-label="
            t('common.chooseParam', { param: t('domainPopulator.avatar') })
          "
          accept="image/png,image/jpeg"
          @select="setAvatarUploaderFile"
          @remove="removeAvatarUploaderFile"
        >
          <template #empty>
            <FontAwesomeIcon
              :icon="['fas', 'cloud-upload']"
              class="upload-icon"
            />
            <p class="upload-instructions text-gray-darkest text-center">
              {{ t('domainPopulator.uploadAvatar') }}
            </p>
          </template>
        </FileUpload>
      </div>
    </InputWrapper>

    <InputWrapper input-id="logo" :label="t('domainPopulator.logo')">
      <div
        v-if="internalValue?.logo"
        class="flex justify-content-between align-items-center"
      >
        <DomainLogo
          :domain-branding="internalValue"
          max-width="60%"
          max-height="150px"
          width="initial"
        />
        <RygenButton
          mode="tertiary"
          icon="pi pi-times"
          :aria-label="
            t('common.removeParam', { param: t('domainPopulator.logo') })
          "
          :severity="Severity.DANGER"
          @click="onRemoveLogo"
        />
      </div>
      <div
        v-else
        class="upload-container flex flex-column justify-content-center align-items-center"
      >
        <FileUpload
          :show-cancel-button="false"
          :show-upload-button="false"
          custom-upload
          :file-limit="1"
          :choose-label="
            t('common.chooseParam', { param: t('domainPopulator.logo') })
          "
          accept="image/png,image/jpeg"
          @select="setLogoUploaderFile"
          @remove="removeLogoUploaderFile"
        >
          <template #empty>
            <FontAwesomeIcon
              :icon="['fas', 'cloud-upload']"
              class="upload-icon"
            />
            <p class="upload-instructions text-gray-darkest text-center">
              {{ t('domainPopulator.uploadLogo') }}
            </p>
          </template>
        </FileUpload>
      </div>
    </InputWrapper>

    <!-- Optional slot for custom actions -->
    <div v-if="$slots.actions" class="domain-branding-actions">
      <slot
        name="actions"
        :save="onSave"
        :reset="onReset"
        :is-loading="isLoading"
        :is-valid="isValid"
      />
    </div>

    <!-- Default action buttons if no custom actions provided -->
    <div v-else-if="showActions" class="domain-branding-actions flex gap-2">
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onReset"
      />
      <RygenButton
        icon="pi pi-save"
        :loading="isLoading"
        :label="saveButtonStatefulLabel"
        :disabled="!isValid"
        @click="onSave"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { matches, useToast, useValidations } from '@rygen/rygen-ui/composables';
import {
  DomainBranding,
  DomainBrandingSignedDocumentReference,
  DomainReference,
  Severity,
} from '@rygen/rygen-ui/model';

import api from '@/api';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DomainAvatar from '@rygen/rygen-ui/components/domain/DomainAvatar.vue';
import DomainLogo from '@rygen/rygen-ui/components/domain/DomainLogo.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenColorPicker from '@rygen/rygen-ui/components/form/RygenColorPicker.vue';
import FileUpload, { FileUploadSelectEvent } from 'primevue/fileupload';

interface Props {
  domain: DomainReference;
  modelValue: DomainBranding;
  showActions?: boolean;
  autoSave?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  domain: () => new DomainReference(),
  modelValue: () => new DomainBranding(),
  showActions: true,
  autoSave: false,
});

const { domain, modelValue, showActions, autoSave } = toRefs(props);

const emit = defineEmits<{
  (event: 'save', newVal: DomainBranding): void;
  (event: 'update:model-value', newVal: DomainBranding): void;
  (event: 'reset'): void;
  (event: 'has-pending-uploads', hasPending: boolean): void;
}>();

const { t } = useI18n();
const toast = useToast();

const internalValue: Ref<DomainBranding> = ref(
  new DomainBranding(modelValue.value),
);
const uploadingImages = ref(false);
const saving = ref(false);

const rules = {
  accentColor: {
    matches: matches(
      /^[abcdefABCDEF0-9]{6}$/,
      t('domainPopulator.accentColor'),
    ),
  },
};

const { v$, validate } = useValidations(internalValue, rules);

const isLoading = computed(() => uploadingImages.value || saving.value);
const isValid = computed(() => !v$.value.$invalid);

const saveButtonStatefulLabel = computed(() => {
  if (uploadingImages.value) {
    return t('common.uploading');
  }

  if (saving.value) {
    return t('common.saving');
  }

  return t('common.save');
});

const avatarUploaderFile = ref(undefined) as Ref<File | undefined>;
const logoUploaderFile = ref(undefined) as Ref<File | undefined>;

// Computed property to check if there are pending uploads
const hasPendingUploads = computed(() => {
  return !!(avatarUploaderFile.value || logoUploaderFile.value);
});

// Watch for changes in pending uploads and emit to parent
watch(
  hasPendingUploads,
  (hasPending) => {
    emit('has-pending-uploads', hasPending);
  },
  { immediate: true },
);

// Auto-upload when domain ID becomes available
watch(
  () => domain.value?.id,
  async (domainId) => {
    if (domainId && hasPendingUploads.value) {
      // Small delay to ensure domain is fully created
      await new Promise((resolve) => setTimeout(resolve, 100));
      await handleAutoUpload();
    }
  },
  { immediate: true },
);

const onRemoveAvatar = () => {
  internalValue.value.avatar = undefined;
  emitChange();
};

const onRemoveLogo = () => {
  internalValue.value.logo = undefined;
  emitChange();
};

const onReset = () => {
  internalValue.value = new DomainBranding(modelValue.value);
  avatarUploaderFile.value = undefined;
  logoUploaderFile.value = undefined;
  emit('reset');
};

const emitChange = () => {
  emit('update:model-value', internalValue.value);

  if (autoSave.value) {
    onSave();
  }
};

const setAvatarUploaderFile = (event: FileUploadSelectEvent) => {
  if (Array.isArray(event.files)) {
    [avatarUploaderFile.value] = event.files;
  } else {
    avatarUploaderFile.value = event.files;
  }
};

const removeAvatarUploaderFile = () => {
  avatarUploaderFile.value = undefined;
};

const setLogoUploaderFile = (event: FileUploadSelectEvent) => {
  if (Array.isArray(event.files)) {
    [logoUploaderFile.value] = event.files;
  } else {
    logoUploaderFile.value = event.files;
  }
};

const removeLogoUploaderFile = () => {
  logoUploaderFile.value = undefined;
};

type TryUploadOptions = {
  fileRef: Ref<File | undefined>;
  afterUpload: (doc: DomainBrandingSignedDocumentReference) => void;
  errorMsg: string;
  paramLabel: string;
};

const tryUpload = async (opts: TryUploadOptions) => {
  try {
    const signedDocumentReference = await uploadFile(
      opts.fileRef.value as File,
    );
    opts.afterUpload(signedDocumentReference);
    opts.fileRef.value = undefined;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(opts.errorMsg, e);
    toast.addError({
      error: e,
      detail: t('errors.uploadingParam', {
        param: opts.paramLabel,
      }),
    });
    throw e; // Re-throw so parent upload logic can handle it
  }
};

// Handle automatic uploads when domain becomes available
const handleAutoUpload = async () => {
  if (!domain.value?.id || !hasPendingUploads.value) {
    return;
  }

  try {
    await performUploads();
    // Success is automatically communicated via has-pending-uploads becoming false
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // Error is already shown via toast in tryUpload
    console.warn('Auto-upload failed:', e);
  }
};

const performUploads = async () => {
  if (!(await validate())) {
    throw new Error('Validation failed');
  }

  saving.value = true;

  try {
    const fileUploads: Promise<void>[] = [];

    if (internalValue.value.avatar === undefined && avatarUploaderFile.value) {
      fileUploads.push(
        tryUpload({
          fileRef: avatarUploaderFile,
          afterUpload: (doc) => (internalValue.value.avatar = doc),
          errorMsg: 'Failed to upload avatar',
          paramLabel: t('domainPopulator.avatar'),
        }),
      );
    }

    if (internalValue.value.logo === undefined && logoUploaderFile.value) {
      fileUploads.push(
        tryUpload({
          fileRef: logoUploaderFile,
          afterUpload: (doc) => (internalValue.value.logo = doc),
          errorMsg: 'Failed to upload logo',
          paramLabel: t('domainPopulator.logo'),
        }),
      );
    }

    if (fileUploads.length) {
      uploadingImages.value = true;
      await Promise.all(fileUploads);
      uploadingImages.value = false;

      // Save the updated branding with file references
      if (domain.value.id) {
        await api.domainPopulator.saveBranding(
          domain.value.id,
          internalValue.value,
        );
      } else {
        throw new Error('Domain ID is required to save branding');
      }

      // Emit change after successful uploads and save to sync with parent
      emitChange();
    }
  } finally {
    saving.value = false;
  }
};

const onSave = async () => {
  try {
    await performUploads();

    if (!isEqual(internalValue.value, modelValue.value)) {
      emit('save', internalValue.value);
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // Error handling is done in performUploads
  }
};

const uploadFile = async (
  file: File,
): Promise<DomainBrandingSignedDocumentReference> => {
  if (!domain?.value.id) {
    throw new Error('Domain id must not be null to perform a file upload');
  }

  const documentReference = new DomainBrandingSignedDocumentReference({
    fileName: file.name,
    contentType: file.type ?? 'application/octet-stream',
  });

  const response = await api.domainPopulator.getUploadUrl(
    domain.value.id,
    documentReference,
  );

  await axios.put(response.uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });

  return new DomainBrandingSignedDocumentReference({
    ...documentReference,
    fileName: response.fileName,
    signedUrl: response.downloadUrl,
  });
};

// Watch for changes in accent color to emit change events
watch(
  () => internalValue.value.accentColor,
  () => {
    emitChange();
  },
);

watch(
  domain,
  (domainVal: DomainReference) => {
    internalValue.value.domainId = domainVal?.id;
  },
  { immediate: true, deep: true },
);

watch(
  modelValue,
  (modelValueVal: DomainBranding) => {
    internalValue.value = new DomainBranding(modelValueVal);
  },
  { immediate: true, deep: true },
);

// Expose methods for parent components (keeping for backward compatibility)
defineExpose({
  save: onSave,
  reset: onReset,
  validate,
  isValid,
  isLoading,
  avatarUploaderFile,
  logoUploaderFile,
});
</script>

<style>
.domain-branding-editor {
  display: flex;
  flex-direction: column;
  gap: var(--gap-2);

  .domain-branding-actions {
    margin-top: var(--gap-2);
  }

  .upload-container {
    :deep(.p-fileupload) {
      width: 100%;

      .p-fileupload-buttonbar {
        width: 100%;
        background: var(--p-surface-50);
        border: 1px solid var(--p-surface-200);
        border-radius: var(--p-border-radius-md);
        padding: var(--sp-4);
        margin-bottom: 0;

        .p-button {
          font-size: 0.875rem;
          padding: 0.5rem 1rem;
          border-radius: var(--p-border-radius-sm);
        }
      }

      .p-fileupload-content {
        width: 100%;
        border: 1px solid var(--p-surface-200);
        border-radius: var(--p-border-radius-md);
        background: var(--p-surface-0);
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;

        .p-progressbar {
          display: none;
        }
      }

      .p-fileupload-empty {
        text-align: center;
        padding: var(--sp-4);

        .upload-icon {
          font-size: 2.5rem;
          color: var(--p-surface-400);
          margin-bottom: var(--sp-2);
        }

        .upload-instructions {
          margin: 0;
          font-size: 0.875rem;
          color: var(--p-surface-500);
        }
      }
    }
  }
}
</style>
